/**
 * Permissions in plain words, grouped by the part of Roger they open.
 *
 * Used where somebody chooses a role and needs to see what it does: each
 * permission becomes a short action under an area ("Billing: view · submit
 * claims · export claims"). The full permission description stays available
 * as a tooltip. A permission missing here still shows, under Other, by its
 * own name, so a new permission is never hidden.
 */

export type PermissionArea =
  | 'intake'
  | 'patients'
  | 'charts'
  | 'visits'
  | 'qa'
  | 'billing'
  | 'payroll'
  | 'reports'
  | 'staff'
  | 'chat'
  | 'settings'
  | 'roger-app'
  | 'security'
  | 'roger'
  | 'other';

/** Display order and headings. */
export const PERMISSION_AREAS: { key: PermissionArea; label: string }[] = [
  { key: 'intake', label: 'Intake' },
  { key: 'patients', label: 'Patients' },
  { key: 'charts', label: 'Charts' },
  { key: 'visits', label: 'Visits' },
  { key: 'qa', label: 'QA & coding' },
  { key: 'billing', label: 'Billing' },
  { key: 'payroll', label: 'Payroll' },
  { key: 'reports', label: 'Reports' },
  { key: 'staff', label: 'Staff' },
  { key: 'chat', label: 'Chat' },
  { key: 'settings', label: 'Settings' },
  { key: 'roger-app', label: 'Roger app' },
  { key: 'security', label: 'Sign-in' },
  { key: 'roger', label: 'Roger staff' },
  { key: 'other', label: 'Other' },
];

const ACTIONS: Record<string, [PermissionArea, string]> = {
  'intake:access': ['intake', 'open intake'],
  'intake:assignable': ['intake', 'be assigned referrals'],
  'intake:export': ['intake', 'export intakes'],
  'intake-columns:edit': ['intake', 'customize intake columns'],
  'eligibility:check': ['intake', 'check eligibility'],
  'referral-sources:view': ['intake', 'see referral sources'],
  'referral-sources:edit': ['intake', 'edit referral sources'],

  'patient-mgr:access': ['patients', 'open patients'],
  'patient-info:edit': ['patients', 'edit patient details'],
  'admissions:edit-source': ['patients', 'correct admission source'],

  'chart:edit-unassigned': ['charts', "edit others' charts at their clinics"],
  'chart:edit-unassigned-agency': ['charts', "edit others' charts anywhere"],
  'chart-import:review': ['charts', 'review imported charts'],
  'episode-manager:edit-unassigned': [
    'charts',
    "edit others' episodes at their clinics",
  ],
  'episode-manager:edit-unassigned-agency': [
    'charts',
    "edit others' episodes anywhere",
  ],
  'episode:amend-closed': ['charts', 'amend closed episodes'],
  'oasis-manager:view': ['charts', 'OASIS manager'],
  'order-manager:view': ['charts', 'order manager'],
  'orders:waive-eval': ['charts', 'waive evaluation orders'],

  'hotbox:access': ['visits', 'open the hotbox'],
  'hotbox:reschedule': ['visits', 'reschedule'],
  'evv:view': ['visits', 'see visit verification'],
  'evv:capture': ['visits', 'capture verification'],
  'evv:remove': ['visits', 'remove verification'],
  'scheduling-points:view-load': ['visits', 'see clinician workload'],
  'clinic:assignable-as-clinician': ['visits', 'be scheduled as clinician'],
  'clinic:assignable-case-manager': ['visits', 'be case manager'],
  'clinic:assignable-as-approver': ['visits', 'approve'],

  'qa:access': ['qa', 'QA'],
  'coding:access': ['qa', 'coding'],
  'coding:force-rerun': ['qa', 'rerun coding'],

  'billing:view': ['billing', 'view'],
  'claims:submit': ['billing', 'submit claims'],
  'claims:reconcile': ['billing', 'reconcile'],
  'billing:export:claims': ['billing', 'export claims'],
  'billing:export:remittances': ['billing', 'export remittances'],
  'billing:export:noas': ['billing', 'export NOAs'],
  'noa-manager:view': ['billing', 'NOA manager'],
  'noa:access': ['billing', 'NOAs'],
  'historical-claims:view': ['billing', 'historical claims'],
  'enrollment:submit': ['billing', 'payer enrollment'],
  'agency-insurance:view': ['billing', 'see insurance providers'],
  'agency-insurance:edit': ['billing', 'edit insurance providers'],

  'payroll:access': ['payroll', 'payroll manager'],
  'worklog:view': ['payroll', 'work log'],
  'hhrg-costs:view': ['payroll', 'see HHRG costs'],
  'hhrg-costs:edit': ['payroll', 'edit HHRG costs'],

  'roger-emr-reports:access': ['reports', 'RogerEMR dashboard'],
  'reports:visits': ['reports', 'visits'],
  'reports:episodes': ['reports', 'episodes'],
  'reports:patients': ['reports', 'patients'],
  'reports:claims': ['reports', 'claims'],
  'reports:user_licenses': ['reports', 'staff licenses'],
  'reports:access': ['reports', 'Roger app usage'],
  'cahps-manager:view': ['reports', 'CAHPS'],

  'clinician-mgr:access': ['staff', 'open the User Manager'],
  'clinician-mgr:invite-users': ['staff', 'invite'],
  'users:deactivate': ['staff', 'deactivate'],
  'users:merge': ['staff', 'merge duplicate accounts'],
  'user-licenses:view': ['staff', 'see licenses'],
  'user-licenses:edit': ['staff', 'edit licenses'],
  'user-license-types:edit': ['staff', 'edit license types'],
  'clinician-mgr:view-internal-roles': ['staff', 'see Roger-only roles'],
  'widgets:users-table:manage': ['staff', 'manage sign-in accounts'],

  'chat:edit-groups': ['chat', 'manage groups'],
  'chat:send-announcement': ['chat', 'send announcements'],

  'external-admin:access': ['settings', 'open admin'],
  'clinic-info:view': ['settings', 'see clinic info'],
  'clinic-info:edit': ['settings', 'edit clinic info'],
  'clinic-physicians:view': ['settings', 'see physicians'],
  'clinic-physicians:edit': ['settings', 'edit physicians'],
  'chart-workflow:view': ['settings', 'see chart workflow'],
  'chart-workflow:edit': ['settings', 'edit chart workflow'],
  'patient-creation-settings:view': ['settings', 'see patient setup'],
  'patient-creation-settings:edit': ['settings', 'edit patient setup'],
  'form-templates:view': ['settings', 'see form templates'],
  'form-templates:edit': ['settings', 'edit form templates'],
  'task-types:view': ['settings', 'see task types'],
  'task-types:edit': ['settings', 'edit task types'],
  'scheduling-points:view-config': ['settings', 'see scheduling points'],
  'scheduling-points:edit-config': ['settings', 'edit scheduling points'],
  'goals:admin-tab': ['settings', 'care plan goals'],
  'emr:manage': ['settings', 'EMR settings'],
  'importer:access': ['settings', 'historical import'],

  'rogerdoc-dashboard:manage': ['roger-app', 'manage users and usage'],
  'customizations:access': ['roger-app', 'customizations'],

  'widgets:sso:manage': ['security', 'single sign-on'],
  'widgets:dsync:manage': ['security', 'directory sync'],
  'widgets:domain-verification:manage': ['security', 'domains'],
  'widgets:audit-log-streaming:manage': ['security', 'audit log streaming'],
  'widgets:api-keys:manage': ['security', 'organisation API keys'],
  'widgets:user-api-keys:manage-all': ['security', "everyone's API keys"],
  'widgets:user-api-keys:manage-self': ['security', 'own API keys'],

  'admin:super-user': ['roger', 'everything'],
  'debug:access': ['roger', 'debug tools'],
};

/**
 * Permissions that let somebody change something, rather than only see it.
 * An area a role reaches only through permissions not listed here reads as
 * "sees"; an unlisted permission therefore never overstates what a role does.
 */
const CHANGES = new Set([
  // Named "view", but the pages they open check orders out and in, and
  // mark OASIS exports.
  'order-manager:view',
  'oasis-manager:view',
  'intake:access',
  'intake:assignable',
  'intake:export',
  'intake-columns:edit',
  'eligibility:check',
  'referral-sources:edit',
  'patient-info:edit',
  'admissions:edit-source',
  'chart:edit-unassigned',
  'chart:edit-unassigned-agency',
  'chart-import:review',
  'episode-manager:edit-unassigned',
  'episode-manager:edit-unassigned-agency',
  'episode:amend-closed',
  'orders:waive-eval',
  'hotbox:reschedule',
  'evv:capture',
  'evv:remove',
  'clinic:assignable-as-clinician',
  'clinic:assignable-case-manager',
  'clinic:assignable-as-approver',
  'qa:access',
  'coding:access',
  'coding:force-rerun',
  'claims:submit',
  'claims:reconcile',
  'enrollment:submit',
  'agency-insurance:edit',
  'payroll:access',
  'hhrg-costs:edit',
  'clinician-mgr:access',
  'clinician-mgr:invite-users',
  'users:deactivate',
  'users:merge',
  'user-licenses:edit',
  'user-license-types:edit',
  'widgets:users-table:manage',
  'chat:edit-groups',
  'chat:send-announcement',
  'clinic-info:edit',
  'clinic-physicians:edit',
  'chart-workflow:edit',
  'patient-creation-settings:edit',
  'form-templates:edit',
  'task-types:edit',
  'scheduling-points:edit-config',
  'goals:admin-tab',
  'emr:manage',
  'importer:access',
  'rogerdoc-dashboard:manage',
  'customizations:access',
  'widgets:sso:manage',
  'widgets:dsync:manage',
  'widgets:domain-verification:manage',
  'widgets:audit-log-streaming:manage',
  'widgets:api-keys:manage',
  'widgets:user-api-keys:manage-all',
  'widgets:user-api-keys:manage-self',
  'admin:super-user',
  'debug:access',
]);

export type PermissionLike = {
  slug: string;
  name: string;
  description?: string | null;
};

export type AreaSummary = {
  key: PermissionArea;
  label: string;
  actions: { slug: string; label: string; description: string }[];
  /** Somebody can change something here, not only see it. */
  changes: boolean;
};

/** Permissions as areas of short actions, in display order. */
export function summarizePermissions(
  permissions: readonly PermissionLike[]
): AreaSummary[] {
  const byArea = new Map<PermissionArea, AreaSummary['actions']>();
  for (const permission of permissions) {
    const [area, label] = ACTIONS[permission.slug] ?? [
      'other',
      permission.name,
    ];
    const actions = byArea.get(area) ?? [];
    actions.push({
      slug: permission.slug,
      label,
      description: permission.description ?? permission.name,
    });
    byArea.set(area, actions);
  }
  return PERMISSION_AREAS.filter(area => byArea.has(area.key)).map(area => {
    // "edit X" says "see X" already, so the pair reads as one action.
    const all = byArea.get(area.key)!;
    const labels = new Set(all.map(a => a.label));
    const actions = all.filter(
      a =>
        !(
          a.label.startsWith('see ') &&
          labels.has(`edit ${a.label.slice('see '.length)}`)
        )
    );
    // In the order this file lists them, which reads view-then-edit.
    const order = Object.keys(ACTIONS);
    actions.sort((a, b) => order.indexOf(a.slug) - order.indexOf(b.slug));
    return {
      ...area,
      actions,
      changes: actions.some(action => CHANGES.has(action.slug)),
    };
  });
}
